// /home/szymon/kreator2/frontend/src/components/EditableText.js
// v6.0.16 - Corrected centering logic integrated into updateMeasurements

import React, { useRef, useState, useEffect, useCallback } from "react";
import { Group, Rect, Text, Line } from "react-konva";
// Importujemy funkcje do prowadnic z właściwego miejsca
import { buildSnapGuides } from "./editor/snapUtils";
import { parseGuideExpression } from "./editor/GuideUtils";

/* -----------------------------------------------------
   Pomocnicze funkcje kursora (bez zmian)
----------------------------------------------------- */
function moveCaretLeft(lines, caretIndex) { if (caretIndex === 0) return caretIndex; const { lineNo, col } = indexToLineCol(caretIndex, lines); if (col > 0) { return caretIndex - 1; } else { if (lineNo > 0) { const newLine = lineNo - 1; const newCol = lines[newLine].length; return lineColToIndex(newLine, newCol, lines); } return caretIndex; } }
function moveCaretRight(lines, caretIndex, totalLen) { if (caretIndex >= totalLen) return caretIndex; const { lineNo, col } = indexToLineCol(caretIndex, lines); if (col < lines[lineNo].length) { return caretIndex + 1; } else { if (lineNo < lines.length - 1) { return lineColToIndex(lineNo + 1, 0, lines); } return caretIndex; } }
function moveCaretUp(lines, caretIndex) { const { lineNo, col } = indexToLineCol(caretIndex, lines); if (lineNo === 0) return caretIndex; const newLine = lineNo - 1; const newCol = Math.min(col, lines[newLine].length); return lineColToIndex(newLine, newCol, lines); }
function moveCaretDown(lines, caretIndex) { const { lineNo, col } = indexToLineCol(caretIndex, lines); if (lineNo >= lines.length - 1) return caretIndex; const newLine = lineNo + 1; const newCol = Math.min(col, lines[newLine].length); return lineColToIndex(newLine, newCol, lines); }
function splitLines(fullText) { return fullText.split("\n"); }
function indexToLineCol(globalIndex, lines) { let remain = globalIndex; let lineNo = 0; let col = 0; for (let i = 0; i < lines.length; i++) { const lnLen = lines[i].length; if (remain <= lnLen) { lineNo = i; col = remain; return { lineNo, col }; } remain -= lnLen + 1; } lineNo = lines.length - 1; col = lines[lineNo].length; return { lineNo, col }; }
function lineColToIndex(lineNo, col, lines) { let idx = 0; for (let i = 0; i < lineNo; i++) { idx += lines[i].length + 1; } idx += col; return idx; }

/* -----------------------------------------------------
   measureLine (bez zmian)
----------------------------------------------------- */
function measureLine(lineStr, fontSize, letterSpacing, fontFamily, lineHeight, isPartial) {
  if (typeof window === "undefined" || !window.Konva) { return { width: 0, height: 0 }; }
  const textNode = new window.Konva.Text({ visible: false });
  const textToUse = (lineStr === "" && isPartial) ? "M" : (lineStr || "M");
  textNode.setAttrs({ text: textToUse, fontSize, letterSpacing, fontFamily, lineHeight, wrap: "none" });
  const rect = textNode.getClientRect({ skipTransform: true });
  return { width: rect.width, height: rect.height };
}

/* -----------------------------------------------------
   getCaretIndexFromCoords (Logika v3 - Sprawdzanie granic znaków)
----------------------------------------------------- */
function getCaretIndexFromCoords( clickX, clickY, lines, lineOffsets, textAlign, fontSize, letterSpacing, fontFamily, lineHeight ) {
    let currentY = 0; let targetLineNo = -1;
    // Znajdź docelową linię na podstawie współrzędnej Y kliknięcia (bez zmian)
    for (let i = 0; i < lines.length; i++) {
        const measured = measureLine(lines[i], fontSize, letterSpacing, fontFamily, lineHeight, false);
        const lineH = measured.height;
        const lineEndY = currentY + lineH;
        if (clickY >= currentY && clickY < lineEndY) { targetLineNo = i; break; }
        if (targetLineNo === -1 && clickY >= currentY - lineH / 2 && clickY < lineEndY + lineH / 2) { targetLineNo = i; }
        currentY = lineEndY;
    }
    if (targetLineNo === -1) { targetLineNo = lines.length - 1; }

    const targetLine = lines[targetLineNo];
    const offsetX = lineOffsets[targetLineNo] || 0;
    const relativeX = clickX - offsetX;

    let calculatedCol = 0; // Domyślna kolumna to 0

    if (targetLine.length === 0) {
        // Jeśli linia jest pusta, kolumna zawsze jest 0
        calculatedCol = 0;
    } else {
        // Sprawdź, czy kliknięcie jest przed środkiem pierwszego znaku
        const firstCharWidth = measureLine(targetLine[0], fontSize, letterSpacing, fontFamily, lineHeight, true).width;
        if (relativeX < firstCharWidth / 2) {
            calculatedCol = 0;
        } else {
            // Iteruj przez znaki, aby znaleźć ten, w którego granicach jest kliknięcie
            let currentX = 0;
            let found = false;
            for (let col = 0; col < targetLine.length; col++) {
                const charWidth = measureLine(targetLine[col], fontSize, letterSpacing, fontFamily, lineHeight, true).width;
                const startX = currentX;
                const endX = currentX + charWidth + (col < targetLine.length - 1 ? letterSpacing : 0); // Dodaj letterSpacing
                const midPoint = startX + charWidth / 2;

                if (relativeX >= startX && relativeX < endX) {
                    // Kliknięcie jest w granicach tego znaku
                    calculatedCol = (relativeX < midPoint) ? col : col + 1;
                    found = true;
                    break;
                }
                currentX = endX - (col < targetLine.length - 1 ? letterSpacing : 0); // Odejmij letterSpacing dla następnej iteracji
            }

            // Jeśli nie znaleziono (kliknięcie za ostatnim znakiem)
            if (!found) {
                const totalWidth = measureLine(targetLine, fontSize, letterSpacing, fontFamily, lineHeight, false).width;
                if (relativeX >= totalWidth) {
                    calculatedCol = targetLine.length;
                } else {
                    // Jeśli nadal nie znaleziono (dziwny przypadek), użyj ostatniej znanej pozycji
                    // lub domyślnej 0 jeśli pętla nie wykonała się ani razu.
                    // calculatedCol pozostaje na ostatniej wartości lub 0.
                    // Można by tu dodać logowanie błędu.
                    console.warn("Could not determine column in getCaretIndexFromCoords, defaulting.");
                }
            }
        }
    }

    // Zwróć globalny indeks
    return lineColToIndex(targetLineNo, calculatedCol, lines);
}


/* -----------------------------------------------------
   EditableText Komponent
----------------------------------------------------- */
export default function EditableText({
  textObj,
  isSelected,
  onSelect,
  onChange,
  stageRef,
  editingTextId,
  setEditingTextId,
  onDragMove,
  onDragEnd,
  draggable = true,
  listening = true,
  onDblClick,
  onDblTap,
  fontsAreLoaded,
  // Dodajemy nowe propsy odebrane z EditorCanvasRenderer
  isPersonalizedProject,
  guides,
  baseWidth,
  baseHeight
}) {
  const isEditing = editingTextId === textObj.id;

  const textNodeRef = useRef(null);

  const [caretIndex, setCaretIndex] = useState((textObj.text || "").length);
  const [caretVisible, setCaretVisible] = useState(false);
  const blinkRef = useRef(null);

  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);

  // Use state for text dimensions, updated by updateMeasurements
  const [textWidth, setTextWidth] = useState(textObj.width || 50);
  const [textHeight, setTextHeight] = useState(textObj.height || 20);
  const [caretCoord, setCaretCoord] = useState({ x: 0, y: 0 });

  const fallbackLineHeight = textObj.lineHeight && textObj.lineHeight > 0 ? textObj.lineHeight : 1.0;
  const fontSize = textObj.fontSize || 14;
  const letterSpacing = textObj.letterSpacing != null ? textObj.letterSpacing : 0;
  const lineHeight = fallbackLineHeight;
  const fontName = textObj.fontName || "sans-serif";
  const textAlign = textObj.alignment || "left";

  let fillColor = "black";
  if (Array.isArray(textObj.color) && textObj.color.length === 3) { fillColor = `rgb(${textObj.color[0]}, ${textObj.color[1]}, ${textObj.color[2]})`; }
  let strokeColor = null;
  if (Array.isArray(textObj.stroke) && textObj.stroke.length === 3) { strokeColor = `rgb(${textObj.stroke[0]}, ${textObj.stroke[1]}, ${textObj.stroke[2]})`; }
  const strokeWidth = textObj.strokeWidth ? textObj.strokeWidth * 0.7 : 0;

  const [lineOffsets, setLineOffsets] = useState([]);
  const [shiftPressed, setShiftPressed] = useState(false);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  // Usunięto initialCenteringDoneRef

  /* --------------- Kursor miganie (bez zmian) --------------- */
  useEffect(() => {
    if (isEditing) { setCaretVisible(true); blinkRef.current = setInterval(() => setCaretVisible((v) => !v), 500); } else { setCaretVisible(false); if (blinkRef.current) { clearInterval(blinkRef.current); blinkRef.current = null; } } return () => { if (blinkRef.current) { clearInterval(blinkRef.current); blinkRef.current = null; } };
  }, [isEditing]);

  /* --------------- Pomiar tekstu (aktualizuje stan textWidth/textHeight i potencjalnie centruje) --------------- */
  const updateMeasurements = useCallback((fullText, cIndex)=>{
    const lines = splitLines(fullText); let maxW=0; let totalH=0; const arrHeights=[]; const arrWidths=[];
    for(let i=0; i< lines.length; i++){ const info = measureLine(lines[i], fontSize, letterSpacing, fontName, lineHeight, false); arrWidths.push(info.width); arrHeights.push(info.height); if(info.width> maxW) maxW= info.width; totalH+= info.height; }
    const finalW= maxW; // Obliczona szerokość bez skalowania
    const finalH= totalH;

    // Aktualizuj stany lokalne
    setTextWidth(finalW);
    setTextHeight(finalH);

    // Oblicz offsety dla każdej linii (potrzebne do kursora i zaznaczenia) - Używamy maxW zamiast finalW
    const offsets = arrWidths.map(w => {
      if (textAlign === "center") return (maxW - w) / 2; // Użyj maxW
      else if (textAlign === "right") return maxW - w; // Użyj maxW
      return 0; // Left align
    });
    setLineOffsets(offsets);

    // Oblicz pozycję kursora
    const { lineNo, col } = indexToLineCol(cIndex, lines);
    let ySum = 0;
    for (let i = 0; i < lineNo; i++) { ySum += arrHeights[i] || 0; }
    let caretX = 0; // Domyślnie 0 dla col = 0
    if (col > 0) { // Oblicz tylko jeśli col > 0
        const partialStr = lines[lineNo].slice(0, col);
        const partInfo = measureLine(partialStr, fontSize, letterSpacing, fontName, lineHeight, true);
        caretX = partInfo.width;
    }
    // Ustaw współrzędne kursora, dodając offset linii
    setCaretCoord({ x: offsets[lineNo] + caretX, y: ySum });


    // Wywołaj onChange dla width/height, jeśli się zmieniły LUB jeśli trzeba wycentrować
    if(onChange){
      let widthChanged = Math.abs((textObj.width || 0) - finalW) > 0.5;
      let heightChanged = Math.abs((textObj.height || 0) - finalH) > 0.5;
      const updatePayload = { id: textObj.id };
      if (widthChanged) updatePayload.width = finalW;
      if (heightChanged) updatePayload.height = finalH;

      // --- USUNIĘTO logikę centrowania z tego miejsca ---
      // Centrowanie odbywa się teraz w EditorState.js po załadowaniu danych.

      // Wywołaj onChange tylko jeśli były zmiany (width lub height)
      if (widthChanged || heightChanged) {
           console.log(`[EditableText updateMeasurements ${textObj.id}] Wywołanie onChange z payload (tylko width/height):`, updatePayload);
           onChange(updatePayload);
      }
    }
  }, [
    textAlign, fontSize, letterSpacing, fontName, lineHeight, textObj.id, textObj.width, textObj.height, // Usunięto zależności x, scaleX, flags, isPersonalizedProject, guides, baseWidth, baseHeight
    onChange, fontsAreLoaded // Pozostawiono fontsAreLoaded, bo wpływa na pomiar
  ]);

  // Efekt do aktualizacji pomiarów przy zmianie tekstu lub edycji
  useEffect(()=>{
    const textToMeasure = textObj.text || "";
    const indexToUse = isEditing ? caretIndex : textToMeasure.length;
    // console.log(`[EditableText ${textObj.id}] updateMeasurements effect triggered. isEditing: ${isEditing}, caretIndex: ${caretIndex}, indexToUse: ${indexToUse}`);
    updateMeasurements(textToMeasure, indexToUse);
  }, [ textObj.id, textObj.text, textObj.color, textObj.stroke, textObj.strokeWidth, textObj.alignment, textObj.fontSize, textObj.fontName, textObj.lineHeight, textObj.letterSpacing, isEditing, caretIndex, updateMeasurements, fontsAreLoaded ]); // Dodano fontsAreLoaded

  // --- USUNIĘTO useEffect dedykowany do centrowania ---

  // Transformer (bez zmian)
  useEffect(()=>{
    if(!stageRef?.current) return; const stage= stageRef.current; const node= stage.findOne(`#${textObj.id}`); if(!node) return; if(isSelected){ const tr= stage.findOne("Transformer"); if(tr){ tr.nodes([node]); tr.getLayer().batchDraw(); } }
  }, [isSelected, stageRef, textObj.id, textWidth, textHeight]);

  // Single click => onSelect (bez zmian)
  const handleClick= useCallback((e)=>{ if(!isSelected){ onSelect?.(textObj.id); } }, [isSelected, onSelect, textObj.id]);

  // Mouse down => obsługa edycji? (bez zmian)
  const handleGroupMouseDown= useCallback((e)=>{
    if(!isEditing) return; e.cancelBubble= true; setIsDraggingSelection(true);
    const container= stageRef?.current?.container?.(); if(container){ container.tabIndex=1; container.focus(); }
    const pos= e.target.getRelativePointerPosition(); if (!pos) return; const lines= splitLines(textObj.text||"");
    const newIndex= getCaretIndexFromCoords( pos.x, pos.y, lines, lineOffsets, textAlign, fontSize, letterSpacing, fontName, lineHeight );
    if(shiftPressed){ setSelectionEnd(newIndex); setCaretIndex(newIndex); } else { setCaretIndex(newIndex); setSelectionStart(newIndex); setSelectionEnd(newIndex); }
  }, [ isEditing, stageRef, textObj.text, lineOffsets, textAlign, fontSize, letterSpacing, fontName, lineHeight, shiftPressed, draggable, setEditingTextId ]);

  // Mouse move => obsługa zaznaczania tekstu (bez zmian)
  const handleGroupMouseMove= useCallback((e)=>{
    if(!isEditing || !isDraggingSelection) return; e.cancelBubble= true;
    const pos= e.target.getRelativePointerPosition(); if (!pos) return; const lines= splitLines(textObj.text||"");
    const newIndex= getCaretIndexFromCoords( pos.x, pos.y, lines, lineOffsets, textAlign, fontSize, letterSpacing, fontName, lineHeight );
    setCaretIndex(newIndex); setSelectionEnd(newIndex);
  }, [ isEditing, isDraggingSelection, textObj.text, lineOffsets, textAlign, fontSize, letterSpacing, fontName, lineHeight ]);

  // Mouse up => zakończ zaznaczanie (bez zmian)
  const handleGroupMouseUp= useCallback((e)=>{ if(!isEditing) return; setIsDraggingSelection(false); }, [isEditing]);

  // Dragging - zaktualizowane, aby przekazywać cały event
  const handleDragMoveEvt = useCallback((e) => {
    if (isEditing) return;
    if (!onDragMove) return;
    onDragMove(e);
  }, [isEditing, onDragMove]);

  // DragEnd - Uproszczono, usunięto logikę przywracania X dla centrowania
  const handleDragEndEvt = useCallback((e) => {
    if (isEditing) return;
    const node = e.target;
    const finalX = node.x();
    const finalY = node.y();
    onChange?.({ id: textObj.id, x: finalX, y: finalY });
    onDragEnd?.(e);
  }, [isEditing, textObj.id, onChange, onDragEnd]);


  // Transform (bez zmian)
  const handleTransformEnd= useCallback((e)=>{ const node= e.target; const scX= node.scaleX(); const scY= node.scaleY(); const rotation= node.rotation(); const x= node.x(); const y= node.y(); const newW= textWidth* scX; const newH= textHeight* scY; node.scaleX(1); node.scaleY(1); onChange?.({ id: textObj.id, x, y, rotation, scaleX: scX, scaleY: scY, width: newW, height: newH }); }, [textWidth, textHeight, textObj.id, onChange]);

  // Key events (bez zmian)
  useEffect(()=>{ const container= stageRef?.current?.container?.(); if(!container) return; const handleKeyDown= (e)=>{ if(!isEditing) return; const val= textObj.text||""; const lines= splitLines(val); const totalLen= val.length; const isCtrl= e.ctrlKey|| e.metaKey; if(e.key==="Shift"){ setShiftPressed(true); } if(isCtrl){ switch(e.key.toLowerCase()){ case "a": { e.preventDefault(); e.stopPropagation(); setSelectionStart(0); setSelectionEnd(val.length); setCaretIndex(val.length); return; } case "c": { e.preventDefault(); e.stopPropagation(); const selS= Math.min(selectionStart, selectionEnd); const selE= Math.max(selectionStart, selectionEnd); const selectedSub= val.slice(selS, selE); if(!selectedSub) return; if(navigator?.clipboard?.writeText){ navigator.clipboard.writeText(selectedSub).catch(()=>{}); } else { const dummy= document.createElement("textarea"); dummy.value= selectedSub; document.body.appendChild(dummy); dummy.select(); try{ document.execCommand("copy");} catch{} document.body.removeChild(dummy); } return; } default: break; } } if(!e.ctrlKey){ switch(e.key){ case "ArrowLeft":{ e.preventDefault(); e.stopPropagation(); const newIndex= moveCaretLeft(lines, caretIndex); if(shiftPressed){ setSelectionEnd(newIndex); setCaretIndex(newIndex); } else { setCaretIndex(newIndex); setSelectionStart(newIndex); setSelectionEnd(newIndex); } return; } case "ArrowRight":{ e.preventDefault(); e.stopPropagation(); const newIndex= moveCaretRight(lines, caretIndex, totalLen); if(shiftPressed){ setSelectionEnd(newIndex); setCaretIndex(newIndex); } else { setCaretIndex(newIndex); setSelectionStart(newIndex); setSelectionEnd(newIndex); } return; } case "ArrowUp":{ e.preventDefault(); e.stopPropagation(); const newIndex= moveCaretUp(lines, caretIndex); if(shiftPressed){ setSelectionEnd(newIndex); setCaretIndex(newIndex); } else { setCaretIndex(newIndex); setSelectionStart(newIndex); setSelectionEnd(newIndex); } return; } case "ArrowDown":{ e.preventDefault(); e.stopPropagation(); const newIndex= moveCaretDown(lines, caretIndex); if(shiftPressed){ setSelectionEnd(newIndex); setCaretIndex(newIndex); } else { setCaretIndex(newIndex); setSelectionStart(newIndex); setSelectionEnd(newIndex); } return; } case "Escape":{ e.preventDefault(); e.stopPropagation(); setEditingTextId(null); return; } case "Enter":{ e.preventDefault(); e.stopPropagation(); const left= val.slice(0, caretIndex); const right= val.slice(caretIndex); const newText= left+ "\n"+ right; onChange?.({ id: textObj.id, text:newText }); const newCar= caretIndex+ 1; setCaretIndex(newCar); setSelectionStart(newCar); setSelectionEnd(newCar); return; } case "Backspace":{ e.preventDefault(); e.stopPropagation(); const selS= Math.min(selectionStart, selectionEnd); const selE= Math.max(selectionStart, selectionEnd); if(selS!== selE){ const left= val.slice(0, selS); const right= val.slice(selE); const newText= left+ right; onChange?.({ id: textObj.id, text:newText }); setCaretIndex(selS); setSelectionStart(selS); setSelectionEnd(selS); } else if(caretIndex>0){ const left= val.slice(0, caretIndex-1); const right= val.slice(caretIndex); const newText= left+ right; onChange?.({ id: textObj.id, text:newText }); const newCar= caretIndex-1; setCaretIndex(newCar); setSelectionStart(newCar); setSelectionEnd(newCar); } return; } default:{
    if(e.key.length===1){
        e.preventDefault();
        e.stopPropagation();
        const selS= Math.min(selectionStart, selectionEnd);
        const selE= Math.max(selectionStart, selectionEnd);
        const left= val.slice(0, selS);
        const right= val.slice(selE);
        const newText= left+ e.key+ right;
        const newCar= selS+ 1;

        // Optymistyczna aktualizacja węzła Konva
        if (textNodeRef.current) {
            textNodeRef.current.text(newText);
        }
        // Wywołaj onChange, aby zaktualizować stan nadrzędny
        onChange?.({ id: textObj.id, text:newText });
        // Zaktualizuj stan lokalny kursora
        setCaretIndex(newCar);
        setSelectionStart(newCar);
        setSelectionEnd(newCar);
        // Bezpośrednio zaktualizuj pomiary po zmianie tekstu
        updateMeasurements(newText, newCar);
    }
 }
 }
 }
 };
    const handleKeyUp= (e)=>{ if(e.key==="Shift"){ setShiftPressed(false); } };
    const handlePaste= (e)=>{ if(!isEditing) return; e.preventDefault(); e.stopPropagation(); const clip= e.clipboardData.getData("text"); if(!clip) return; const val= textObj.text||""; const selS= Math.min(selectionStart, selectionEnd); const selE= Math.max(selectionStart, selectionEnd); const left= val.slice(0, selS); const right= val.slice(selE); const newText= left+ clip+ right; onChange?.({ id: textObj.id, text:newText }); const newCar= selS+ clip.length; setCaretIndex(newCar); setSelectionStart(newCar); setSelectionEnd(newCar); };
    container.addEventListener("keydown", handleKeyDown); container.addEventListener("keyup", handleKeyUp); container.addEventListener("paste", handlePaste);
    return ()=>{ container.removeEventListener("keydown", handleKeyDown); container.removeEventListener("keyup", handleKeyUp); container.removeEventListener("paste", handlePaste); };
  }, [ stageRef, isEditing, caretIndex, textObj, onChange, setEditingTextId, shiftPressed, selectionStart, selectionEnd, updateMeasurements ]);

  // Obsługa startu edycji (bez zmian)
  useEffect(() => {
    if (isEditing) {
      updateMeasurements(textObj.text || "", caretIndex);
      const container = stageRef?.current?.container?.();
      if (container) {
          container.tabIndex = 1;
          container.focus();
      }
    }
  }, [isEditing, stageRef, textObj.text, caretIndex, updateMeasurements]);

  // --- USUNIĘTO useEffect dedykowany do centrowania ---


  /* --------------- Rysowanie zaznaczenia (bez zmian) --------------- */
  function renderSelection(){
    if(!isEditing || selectionStart === selectionEnd) return null;

    const selS = Math.min(selectionStart, selectionEnd);
    const selE = Math.max(selectionStart, selectionEnd);
    const fullText = textObj.text || "";
    const lines = splitLines(fullText);
    const selectionRects = [];
    let textNode = textNodeRef.current;

    if (!textNode) {
      console.warn("[renderSelection] textNodeRef is null, cannot draw selection yet.");
      return null;
    }

    let currentY = 0;
    let charIndex = 0;

    for (let i = 0; i < lines.length; i++) {
        const lineText = lines[i];
        const lineLength = lineText.length;
        const lineStartIndex = charIndex;
        const lineEndIndex = charIndex + lineLength;

        if (selE > lineStartIndex && selS < lineEndIndex + 1) {
            const startCharInLine = Math.max(0, selS - lineStartIndex);
            const endCharInLine = Math.min(lineLength, selE - lineStartIndex);

            if (startCharInLine < endCharInLine) {
                const offsetX = lineOffsets[i] || 0;

                const textBeforeSelection = lineText.substring(0, startCharInLine);
                const selectedText = lineText.substring(startCharInLine, endCharInLine);

                const tempTextNode = new window.Konva.Text({
                    text: 'M',
                    fontSize: fontSize,
                    fontFamily: fontName,
                    lineHeight: lineHeight,
                    letterSpacing: letterSpacing,
                    visible: false
                });

                const lineHeightPx = tempTextNode.height();

                tempTextNode.text(textBeforeSelection);
                const xStart = offsetX + tempTextNode.getTextWidth();

                tempTextNode.text(selectedText);
                const selectionWidth = tempTextNode.getTextWidth();

                selectionRects.push(
                    <Rect
                        key={`sel-${i}`}
                        x={xStart}
                        y={currentY}
                        width={selectionWidth}
                        height={lineHeightPx}
                        fill="rgba(173, 216, 230, 0.7)"
                        listening={false}
                    />
                );
            }
        }
        const measuredLine = measureLine(lineText, fontSize, letterSpacing, fontName, lineHeight, false);
        currentY += measuredLine.height;
        charIndex += lineLength + 1;
    }

    return selectionRects;
  }

  // --- Główny Render komponentu ---
  return (
    <Group
      id={textObj.id}
      x={textObj.x} // Pozycja X grupy pochodzi z danych (powinna być poprawna dla centrowania)
      y={textObj.y}
      rotation={textObj.rotation||0}
      scaleX={textObj.scaleX||1}
      scaleY={textObj.scaleY||1}
      draggable={!isEditing && draggable}
      listening={listening}
      onClick={handleClick} onTap={handleClick}
      onMouseDown={handleGroupMouseDown}
      onMouseMove={handleGroupMouseMove}
      onMouseUp={handleGroupMouseUp}
      onDragMove={handleDragMoveEvt} onDragEnd={handleDragEndEvt}
      onTransformEnd={handleTransformEnd}
      onDblClick={(e)=>{
        if(e.target.isDragging()) return; if(onDblClick){ onDblClick(e); }
        else { setEditingTextId?.(textObj.id); const pos= e.target.getRelativePointerPosition(); if (!pos) return; const lines= splitLines(textObj.text||""); const newIndex= getCaretIndexFromCoords( pos.x, pos.y, lines, lineOffsets, textAlign, fontSize, letterSpacing, fontName, lineHeight ); setCaretIndex(newIndex); setSelectionStart(newIndex); setSelectionEnd(newIndex); }
      }}
      onDblTap={(e)=>{
        if(e.target.isDragging()) return; if(onDblTap){ onDblTap(e); }
        else { setEditingTextId?.(textObj.id); const pos= e.target.getRelativePointerPosition(); if (!pos) return; const lines= splitLines(textObj.text||""); const newIndex= getCaretIndexFromCoords( pos.x, pos.y, lines, lineOffsets, textAlign, fontSize, letterSpacing, fontName, lineHeight ); setCaretIndex(newIndex); setSelectionStart(newIndex); setSelectionEnd(newIndex); }
      }}
      perfectDrawEnabled={false}
    >
      {/* Przywrócono Niewidzialny prostokąt tła - może być potrzebny do hit detection */}
      <Rect x={0} y={0} width={textWidth} height={textHeight} fill="transparent" listening={true} perfectDrawEnabled={false} />
      {/* Renderowanie zaznaczenia */}
      {isEditing && renderSelection()}
      {/* Główny obiekt Tekstowy Konva */}
      <Text
        ref={textNodeRef}
        x={0} y={0} // Pozycja tekstu względem grupy jest zawsze 0,0
        text={textObj.text} fontSize={fontSize} letterSpacing={letterSpacing}
        fontFamily={fontName} lineHeight={lineHeight} align={textAlign}
        fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth} wrap="none" listening={false}
        perfectDrawEnabled={false}
        // Usunięto offsetX - pozycjonowanie odbywa się przez Group.x
        offsetX={0}
        offsetY={0}
      />
      {/* Kursor (caret) - używa obliczonych caretCoord */}
      {isEditing && caretVisible && selectionStart=== selectionEnd && (
        <Line x={caretCoord.x} y={caretCoord.y} points={[0,0, 0, fontSize* lineHeight* 0.98]} stroke="black" strokeWidth={1} listening={false} />
      )}
    </Group>
  );
}
